import { Button } from '@shared/components/common/Button';
import { UserPublication } from '@shared/types/profile';
import { cn } from '@shared/utils/utils';
import { BookOpenIcon, HeartIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@shared/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { showConfirmReadModal } from '@user/components/publication/ConfirmReadModal';
import { flowResult } from 'mobx';
import defaultCover from '@shared/assets/images/default-cover.png';
import { PUBLICATION_TYPE } from '@shared/types/enums';
import { formatDateTime } from '@shared/utils/datetime';
import { DATE_TIME_FULL, DATE_YMD } from '@shared/components/constants/common.constants';

interface PublicationCardProps {
    publication: UserPublication,
    onToggleFavoriteSuccess?: () => void,
    type?: 'default' | 'history',
    onReadPublicationWithKeyword?: (publication: UserPublication) => void
}

const PublicationCard = observer(function PublicationCard({ publication, onToggleFavoriteSuccess, type = 'default', onReadPublicationWithKeyword }: PublicationCardProps) {
    const navigate = useNavigate();
    const { profileStore: { toggleFavorite }, modalStore, publicationStore, authStore: { profile, getProfile } } = useStore();
    const [isToggling, setIsToggling] = useState(false);
    const [localIsFavorited, setLocalIsFavorited] = useState(publication.isFavorited);

    // Sync local state with publication when publication changes
    useEffect(() => {
        setLocalIsFavorited(publication.isFavorited);
    }, [publication.isFavorited]);

    const handleRedirectToPublicationDetail = () => {
        navigate(`/publications/${publication.id}`);
    };

    const isFavorited = localIsFavorited;

    const handleToggleFavorite = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isToggling) return;

        setIsToggling(true);
        try {
            const actionType = isFavorited ? 'remove' : 'add';
            const success = await toggleFavorite(publication.id, actionType);
            if (success) {
                setLocalIsFavorited(!isFavorited);
                onToggleFavoriteSuccess?.();
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        } finally {
            setIsToggling(false);
        }
    };

    const handleReadPublication = async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!publication.owned) {
            const startDate = profile?.package?.startDate;
            const expirationDate = profile?.package?.expirationDate;
            const remainSelected = profile?.packageSelected?.remainSelected || 0;
            const isValidated = startDate && expirationDate && new Date(startDate) <= new Date() && new Date(expirationDate) > new Date();
            if (!remainSelected || !isValidated) {
                modalStore.showAlertModal({
                    id: 'no-selected-modal',
                    content: 'このコンテンツを閲覧する権限がありません。',
                    footerClassName: 'sm:justify-center',
                    cancelButtonClassName: 'hidden'
                });
                return;
            }

            showConfirmReadModal(modalStore, async () => {
                const result = await flowResult(publicationStore.selectPublication(publication.id));
                if (result) {
                    await flowResult(getProfile());
                    onReadPublicationWithKeyword?.(publication);
                }
            });
            return;
        }
        onReadPublicationWithKeyword?.(publication);
    };

    const isMagazine = publication.type === PUBLICATION_TYPE.MagazineIssue;
    const isShowSearchCount = publication.searchCount !== undefined;
    return (
        <article className='flex w-full h-full p-4 flex-col gap-3 bg-white rounded-lg shadow-md overflow-hidden group hover:shadow-lg transition-all cursor-pointer' onClick={handleRedirectToPublicationDetail}>
            {/* Book Cover Image - Fixed height */}
            <div className='relative aspect-[3/4] overflow-hidden bg-gray-100 flex-shrink-0'>
                <img
                    src={publication.imgUrl}
                    alt={publication.title}
                    className='size-full object-cover'
                    onError={(e) => {
                        e.currentTarget.src = defaultCover;
                    }}
                />
                <Button
                    variant='ghost'
                    size='icon'
                    className={cn('absolute top-1.5 right-1.5 rounded-full size-7 bg-black/40 text-white hover:scale-150 origin-top-right transition-all disabled:opacity-50', isFavorited && 'bg-[#EFEFEF]')}
                    onClick={handleToggleFavorite}
                    disabled={isToggling}
                >
                    <HeartIcon className={cn('size-4', isFavorited ? 'fill-red-500 stroke-red-500' : 'fill-transparent')} />
                </Button>

                <Button
                    variant='ghost'
                    size='icon'
                    className='absolute bottom-1.5 right-1.5 rounded-full size-7 bg-black/40 text-white hover:scale-150 hover:text-primary origin-bottom-right transition-all'
                    onClick={handleReadPublication}

                >
                    <BookOpenIcon className='size-4' />
                </Button>
            </div>

            {/* Book Info - Fixed min-height */}
            <div className='space-y-2.5 flex-1 flex flex-col'>
                <h4 className='text-sm font-medium line-clamp-2 leading-snug group-hover:text-primary transition-all'>
                    {publication.title}
                </h4>

                <div className='flex-1 flex flex-col gap-2.5 justify-end'>
                    {publication.author && !isMagazine && (
                        <p className='text-xs text-text-secondary line-clamp-1'>
                            著者：
                            {publication.author}
                        </p>
                    )}

                    {type === 'history' ?
                        (
                            <>

                                <p className='text-xs text-text-secondary'>
                                    最終閲覧日 :
                                    {' '}
                                    {publication.readTime ? `${formatDateTime(publication.readTime, DATE_TIME_FULL)}` : ''}
                                </p>
                                <p className='text-xs text-text-secondary'>
                                    閲覧回数 :
                                    {' '}
                                    {publication.readNumber}
                                </p>
                            </>
                        ) :
                        (
                            <p className='text-xs text-text-secondary'>
                                発行日：
                                {formatDateTime(publication.releaseDate, DATE_YMD, { returnDefault: '不明' })}
                            </p>
                        )}
                    {isShowSearchCount && (
                        <p className='text-xs text-primary font-semibold'>
                            {publication.searchCount}
                            件の結果が見つかりました
                        </p>
                    )}
                </div>
            </div>
        </article>
    );
});

export default PublicationCard;
